// Layout customizável da home do aluno (v4 — linhas independentes).
//
// MUDANÇA v4: o layout vira `rows: HomeBlockId[][]` — array de linhas, cada
// linha com seus próprios blocos. A largura de cada bloco no desktop é
// derivada do tamanho da linha (linha com 3 blocos → 3 colunas iguais).
// Isso permite layouts mistos, ex:
//   Linha 1: 3 cards pequenos (notif/eventos/mensagens)
//   Linha 2: 1 card grande (aiStudy)
//   Linha 3: 2 cards (pagamentos + destaque)
//   Linha 4: banks fullWidth
//
// Mobile: cada linha vira um stack de 1 coluna (independente do desktop).
// Drag-n-drop: 1 DndContext + 1 SortableContext POR linha; blocos podem
// trocar de linha arrastando.
//
// Backcompat: layouts v2/v3 (com `order` + `cols`) viram `rows` quebrando
// `order` em grupos do tamanho de `cols`, com blocos fullWidth em linhas
// próprias.

import { signal } from "@preact/signals";

export type HomeBlockId =
  | "notifications"
  | "eventos"
  | "mensagens"
  | "aiStudy"
  | "pagamentos"
  | "destaque"
  | "simulacoes"
  | "gruposEstudo"
  | "banks" // full-width
  | "turmasDisciplinas"; // full-width

export interface HomeBlockMeta {
  id: HomeBlockId;
  label: string;
  fullWidth?: boolean;
}

export const ALL_HOME_BLOCKS: HomeBlockMeta[] = [
  { id: "notifications", label: "Notificações" },
  { id: "eventos", label: "Próximos eventos" },
  { id: "mensagens", label: "Mensagens" },
  { id: "aiStudy", label: "Estudando com IA" },
  { id: "pagamentos", label: "Pagamentos" },
  { id: "destaque", label: "Destaque (prova ativa)" },
  { id: "simulacoes", label: "Simulações recentes" },
  { id: "gruposEstudo", label: "Grupos de Estudo" },
  { id: "banks", label: "Bancos de questões", fullWidth: true },
  { id: "turmasDisciplinas", label: "Turmas e disciplinas", fullWidth: true }
];

const ALL_IDS: HomeBlockId[] = ALL_HOME_BLOCKS.map((b) => b.id);
const FULL_WIDTH_SET = new Set(
  ALL_HOME_BLOCKS.filter((b) => b.fullWidth).map((b) => b.id)
);

export function isFullWidthBlock(id: HomeBlockId): boolean {
  return FULL_WIDTH_SET.has(id);
}

/** Tamanho de cada bloco no grid (desktop). Cada linha tem 6 col-slots; os
 *  spans abaixo somam <=6 dentro da linha (overflow envelopa). Mobile sempre
 *  ignora — todo bloco vira span-6 (1 coluna). */
export type BlockSize = "sm" | "md" | "lg" | "xl";

/** col-span no grid de 6 colunas por size */
export const BLOCK_SIZE_SPAN: Record<BlockSize, number> = {
  sm: 2, // 3 por linha
  md: 3, // 2 por linha
  lg: 4, // 1.5 por linha
  xl: 6 // 1 por linha (full)
};

interface HomeLayout {
  /** Cada linha é uma lista de blocos. O size de cada bloco controla quanto
   *  ele ocupa do grid de 6 cols da linha. */
  rows: HomeBlockId[][];
  /** Tamanho de cada bloco. Defaults definidos em DEFAULT_SIZE_FOR. */
  sizes: Partial<Record<HomeBlockId, BlockSize>>;
  /** Blocos ocultos não aparecem em nenhuma linha. */
  hidden: HomeBlockId[];
}

/** Size default por bloco. Cards de listagem (banks/turmas) começam xl,
 *  cards compactos (notif/eventos/mensagens) começam sm, mid-sized começam
 *  md. Usuário pode redimensionar livremente depois. */
const DEFAULT_SIZE_FOR: Record<HomeBlockId, BlockSize> = {
  notifications: "sm",
  eventos: "sm",
  mensagens: "sm",
  aiStudy: "xl",
  pagamentos: "sm",
  destaque: "sm",
  simulacoes: "sm",
  gruposEstudo: "md",
  banks: "xl",
  turmasDisciplinas: "xl"
};

export function getBlockSize(id: HomeBlockId): BlockSize {
  // fullWidth força xl independente do que tiver salvo
  if (isFullWidthBlock(id)) return "xl";
  return homeLayout.value.sizes[id] ?? DEFAULT_SIZE_FOR[id] ?? "md";
}

export function getBlockSpan(id: HomeBlockId): number {
  return BLOCK_SIZE_SPAN[getBlockSize(id)];
}

const HOME_LAYOUT_KEY = "student-home-layout-v4";

// Foco do aluno padrão = Estudando com IA + Bancos. Pedido 2026-05-22: as
// duas caixas principais ficam no topo (cada uma em linha própria, full-width),
// depois vem o resto. Layout antigo preservado pros 4 admins via
// seed-admin-menus-flags (que popula prefs.homeLayout pra eles).
const DEFAULT_LAYOUT: HomeLayout = {
  rows: [
    ["aiStudy"], // 1 × xl = 6 (cheia) — caixa principal de IA
    ["banks"], // fullWidth — caixa principal de bancos
    ["notifications", "eventos", "mensagens"], // 3 × sm = 6 (cheia)
    ["pagamentos", "destaque", "simulacoes"], // 3 × sm = 6 (cheia)
    ["turmasDisciplinas"] // fullWidth
  ],
  sizes: {},
  hidden: []
};

// Mapeamento v2 → v3 (IDs antigos compostos viram múltiplos atômicos)
const LEGACY_GROUPS: Record<string, HomeBlockId[]> = {
  compactRow: ["notifications", "eventos", "mensagens"],
  actionRow: ["pagamentos", "destaque", "simulacoes"]
};

function normalizeBlockIds(raw: unknown): HomeBlockId[] {
  if (!Array.isArray(raw)) return [];
  const out: HomeBlockId[] = [];
  const seen = new Set<HomeBlockId>();
  for (const item of raw) {
    if (typeof item !== "string") continue;
    if (item in LEGACY_GROUPS) {
      for (const id of LEGACY_GROUPS[item]) {
        if (!seen.has(id)) {
          out.push(id);
          seen.add(id);
        }
      }
      continue;
    }
    if (ALL_IDS.includes(item as HomeBlockId)) {
      const id = item as HomeBlockId;
      if (!seen.has(id)) {
        out.push(id);
        seen.add(id);
      }
    }
  }
  return out;
}

/** Converte ordem linear + cols pra estrutura de rows. fullWidth sempre
 *  vira linha própria — independente de cols. */
function linearToRows(order: HomeBlockId[], cols: 1 | 2 | 3): HomeBlockId[][] {
  const rows: HomeBlockId[][] = [];
  let buffer: HomeBlockId[] = [];
  for (const id of order) {
    if (isFullWidthBlock(id)) {
      if (buffer.length > 0) {
        rows.push(buffer);
        buffer = [];
      }
      rows.push([id]);
      continue;
    }
    buffer.push(id);
    if (buffer.length >= cols) {
      rows.push(buffer);
      buffer = [];
    }
  }
  if (buffer.length > 0) rows.push(buffer);
  return rows;
}

/** Limpa rows vindas do disco: dedupe entre linhas, drop ids inválidos,
 *  remove linhas vazias, garante que todo bloco visível existe. */
function sanitizeRows(rows: unknown, hidden: Set<HomeBlockId>): HomeBlockId[][] {
  if (!Array.isArray(rows)) return [];
  const seen = new Set<HomeBlockId>();
  const out: HomeBlockId[][] = [];
  for (const row of rows) {
    if (!Array.isArray(row)) continue;
    const cleaned: HomeBlockId[] = [];
    for (const item of row) {
      if (typeof item !== "string") continue;
      const id = item as HomeBlockId;
      if (!ALL_IDS.includes(id)) continue;
      if (seen.has(id)) continue;
      if (hidden.has(id)) continue;
      seen.add(id);
      // fullWidth força linha própria — se vier em row mista, separa
      if (isFullWidthBlock(id) && cleaned.length > 0) {
        out.push(cleaned.splice(0, cleaned.length));
        out.push([id]);
        continue;
      }
      cleaned.push(id);
      if (isFullWidthBlock(id)) {
        // Não aceita mais nada nessa linha; quebra aqui
        out.push(cleaned.splice(0, cleaned.length));
      }
    }
    if (cleaned.length > 0) out.push(cleaned);
  }
  // Adiciona blocos ausentes em linhas próprias no fim
  for (const id of ALL_IDS) {
    if (seen.has(id) || hidden.has(id)) continue;
    out.push([id]);
  }
  return out;
}

function readLayout(): HomeLayout {
  try {
    // v4 — formato atual
    const rawV4 = localStorage.getItem(HOME_LAYOUT_KEY);
    if (rawV4) {
      const parsed = JSON.parse(rawV4) as Partial<HomeLayout>;
      const hidden = new Set(normalizeBlockIds(parsed.hidden));
      const rows = sanitizeRows(parsed.rows, hidden);
      const sizes = sanitizeSizes(parsed.sizes);
      return { rows, sizes, hidden: Array.from(hidden) };
    }

    // v3 backcompat (order + hidden + cols)
    const rawV3 = localStorage.getItem("student-home-layout-v3");
    if (rawV3) {
      const parsed = JSON.parse(rawV3) as {
        order?: unknown;
        hidden?: unknown;
        cols?: unknown;
      };
      const hidden = normalizeBlockIds(parsed.hidden);
      const hiddenSet = new Set(hidden);
      const order = normalizeBlockIds(parsed.order).filter(
        (id) => !hiddenSet.has(id)
      );
      const colsRaw = Number(parsed.cols);
      const cols: 1 | 2 | 3 = colsRaw === 2 || colsRaw === 3 ? colsRaw : 1;
      const rows = linearToRows(order, cols);
      return { rows, sizes: {}, hidden };
    }

    // v2 (compactRow/actionRow ainda no order)
    const rawV2 = localStorage.getItem("student-home-layout-v2");
    if (rawV2) {
      const parsed = JSON.parse(rawV2) as {
        order?: unknown;
        hidden?: unknown;
        cols?: unknown;
      };
      const hidden = normalizeBlockIds(parsed.hidden);
      const hiddenSet = new Set(hidden);
      const order = normalizeBlockIds(parsed.order).filter(
        (id) => !hiddenSet.has(id)
      );
      const colsRaw = Number(parsed.cols);
      const cols: 1 | 2 | 3 = colsRaw === 2 || colsRaw === 3 ? colsRaw : 1;
      const rows = linearToRows(order, cols);
      return { rows, sizes: {}, hidden };
    }

    return cloneDefault();
  } catch {
    return cloneDefault();
  }
}

function cloneDefault(): HomeLayout {
  return {
    rows: DEFAULT_LAYOUT.rows.map((r) => [...r]),
    sizes: { ...DEFAULT_LAYOUT.sizes },
    hidden: [...DEFAULT_LAYOUT.hidden]
  };
}

function sanitizeSizes(raw: unknown): Partial<Record<HomeBlockId, BlockSize>> {
  if (!raw || typeof raw !== "object") return {};
  const out: Partial<Record<HomeBlockId, BlockSize>> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (!ALL_IDS.includes(k as HomeBlockId)) continue;
    if (v === "sm" || v === "md" || v === "lg" || v === "xl") {
      out[k as HomeBlockId] = v;
    }
  }
  return out;
}

function writeLayout(l: HomeLayout): void {
  try {
    localStorage.setItem(HOME_LAYOUT_KEY, JSON.stringify(l));
  } catch {
    /* ignore */
  }
}

export const homeLayout = signal<HomeLayout>(readLayout());

// ============================================================================
// Sync com server (users.prefs.homeLayout). Fire-and-forget.
// ============================================================================

let serverSyncEnabled = false;

export function applyServerHomeLayout(remote: unknown): void {
  if (!remote || typeof remote !== "object") return;
  const r = remote as {
    rows?: unknown;
    hidden?: unknown;
    order?: unknown;
    cols?: unknown;
    sizes?: unknown;
  };
  let next: HomeLayout;
  if (Array.isArray(r.rows)) {
    const hidden = new Set(normalizeBlockIds(r.hidden));
    next = {
      rows: sanitizeRows(r.rows, hidden),
      sizes: sanitizeSizes(r.sizes),
      hidden: Array.from(hidden)
    };
  } else {
    const hidden = normalizeBlockIds(r.hidden);
    const hiddenSet = new Set(hidden);
    const order = normalizeBlockIds(r.order).filter((id) => !hiddenSet.has(id));
    const colsRaw = Number(r.cols);
    const cols: 1 | 2 | 3 = colsRaw === 2 || colsRaw === 3 ? colsRaw : 1;
    next = { rows: linearToRows(order, cols), sizes: {}, hidden };
  }
  homeLayout.value = next;
  writeLayout(next);
}

export function enableHomeLayoutServerSync(): void {
  serverSyncEnabled = true;
}

async function pushToServer(l: HomeLayout): Promise<void> {
  if (!serverSyncEnabled) return;
  try {
    await fetch("/api/user/account", {
      method: "PATCH",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ prefs: { homeLayout: l } })
    });
  } catch {
    /* ignore */
  }
}

/** Lista ordenada de blocos visíveis (achatamento das linhas). Usada pra
 *  rendering linear em mobile e pra iteração de visibilidade. */
export function getVisibleOrderedBlocks(): HomeBlockId[] {
  return homeLayout.value.rows.flat();
}

export function isHomeBlockVisible(id: HomeBlockId): boolean {
  return !homeLayout.value.hidden.includes(id);
}

function commitLayout(next: HomeLayout): void {
  homeLayout.value = next;
  writeLayout(next);
  void pushToServer(next);
}

/** Toggle visibilidade. Esconder remove o bloco de qualquer linha; mostrar
 *  adiciona o bloco em linha nova no fim. */
export function toggleHomeBlock(id: HomeBlockId): void {
  const l = homeLayout.value;
  const hiddenSet = new Set(l.hidden);
  if (hiddenSet.has(id)) {
    hiddenSet.delete(id);
    const rows = l.rows.map((r) => [...r]);
    rows.push([id]);
    commitLayout({ ...l, rows, hidden: Array.from(hiddenSet) });
  } else {
    hiddenSet.add(id);
    const rows = l.rows
      .map((row) => row.filter((bid) => bid !== id))
      .filter((row) => row.length > 0);
    commitLayout({ ...l, rows, hidden: Array.from(hiddenSet) });
  }
}

/** Move um bloco pra uma posição específica (rowIndex, colIndex). Aceita
 *  rowIndex >= rows.length pra criar linha nova no fim. */
export function moveHomeBlock(
  blockId: HomeBlockId,
  toRow: number,
  toCol: number
): void {
  const l = homeLayout.value;
  // Encontra posição atual
  let fromRow = -1;
  let fromCol = -1;
  for (let r = 0; r < l.rows.length; r++) {
    const c = l.rows[r].indexOf(blockId);
    if (c >= 0) {
      fromRow = r;
      fromCol = c;
      break;
    }
  }
  if (fromRow === -1) return;

  // Remove da origem
  const rows = l.rows.map((row) => [...row]);
  rows[fromRow].splice(fromCol, 1);

  // Cria nova linha vazia no fim se necessário
  while (rows.length <= toRow) rows.push([]);

  // fullWidth sempre fica sozinho — força linha própria
  if (isFullWidthBlock(blockId)) {
    const clean = rows.filter((row) => row.length > 0);
    clean.splice(Math.min(toRow, clean.length), 0, [blockId]);
    commitLayout({ ...l, rows: clean });
    return;
  }

  if (rows[toRow]?.length === 1 && isFullWidthBlock(rows[toRow][0])) {
    rows.splice(toRow, 0, [blockId]);
  } else {
    const targetRow = rows[toRow];
    const insertAt = Math.max(0, Math.min(toCol, targetRow.length));
    targetRow.splice(insertAt, 0, blockId);
  }

  const clean = rows.filter((row) => row.length > 0);
  commitLayout({ ...l, rows: clean });
}

/** Muda o tamanho (snap) de um bloco. */
export function setHomeBlockSize(id: HomeBlockId, size: BlockSize): void {
  const l = homeLayout.value;
  if (isFullWidthBlock(id)) return; // fullWidth não respeita resize
  const sizes = { ...l.sizes, [id]: size };
  commitLayout({ ...l, sizes });
}

/** Cria linha nova vazia no fim. Útil pra UX de "puxar bloco pra criar
 *  linha nova". */
export function appendEmptyRow(): void {
  const l = homeLayout.value;
  // Não cria se a última já é vazia
  if (l.rows[l.rows.length - 1]?.length === 0) return;
  commitLayout({ ...l, rows: [...l.rows.map((r) => [...r]), []] });
}

export function resetHomeLayout(): void {
  commitLayout(cloneDefault());
}

// Modo "edição" — quando true, blocos viram draggable + checkbox.
export const homeEditMode = signal<boolean>(false);

export function isDesktopViewport(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(min-width: 768px)").matches;
}
